import {
  getTapToPayAvailability,
  getTapToPayDevice,
  getTapToPayProductName,
  type TapToPayAvailability,
  type TapToPayDevice,
  tapToPayNeedsActivation,
} from "@/utils/tap-to-pay/sumup";
import { sentryLog } from "@/utils/sentry-utils";
import { useCallback, useEffect, useState } from "react";

/**
 * Whether this phone can take Tap to Pay payments, its install id when it can,
 * and on iPhone the product name Apple wants shown.
 */
export function useTapToPayDevice() {
  const [availability, setAvailability] = useState<TapToPayAvailability | null>(
    null,
  );
  const [device, setDevice] = useState<TapToPayDevice | null>(null);
  const [productName, setProductName] = useState<string | null>(null);

  const check = useCallback(async () => {
    const result = await getTapToPayAvailability();
    if (!result.available) {
      setAvailability(result);
      setDevice(null);
      setProductName(null);
      return;
    }
    try {
      setDevice(await getTapToPayDevice());
      setProductName(await getTapToPayProductName());
      setAvailability(result);
    } catch (error) {
      sentryLog.warn("Tap to Pay device id unavailable", {
        cause: error instanceof Error ? error.message : String(error),
      });
      setDevice(null);
      setProductName(null);
      setAvailability({ available: false, reason: "app_update_required" });
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  return {
    availability,
    device,
    productName,
    needsActivation: tapToPayNeedsActivation(),
    recheck: check,
  };
}
